import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { canProbeMediaDuration } from './helpers/canProbeMediaDuration';
import { defaultNameResolver } from './helpers/defaultNameResolver';
import type { TFileWithMeta } from './types/TFileWithMeta';
import type { TUploadConfig, TUploadMode } from './types/TUploadConfig';
import type { TUploadStatus } from './types/TUploadStatus';
export type TUploadState = { files: TFileWithMeta[]; mode: TUploadMode; isUploading: boolean };
export type TUploadActions = {
  attachExisting: (items: Array<Partial<TFileWithMeta> & { meta: Partial<TFileWithMeta['meta']> }>) => void;
  addFiles: (files: File[]) => Promise<void>;
  removeFile: (id: string, opts?: { purge?: boolean }) => void;
  reorderFiles: (orderedIds: string[]) => void;
  mergeWithServerFiles: (serverFiles: TFileWithMeta[]) => void;
  startUpload: (id: string) => Promise<void>;
  startAll: () => Promise<void>;
  reset: () => void;
  setExtra: (id: string, extra: Record<string, any>) => void;
  updateMeta: (id: string, meta: Partial<TFileWithMeta['meta']>) => void;
};

const UploadContext = createContext<(TUploadState & TUploadActions) | null>(null);

export const UploadProvider = ({ config, children }: React.PropsWithChildren<{ config: TUploadConfig }>) => {
  const [files, setFiles] = useState<TFileWithMeta[]>([]);
  const [mode] = useState<TUploadMode>(config.mode || 'deferred');
  const [isUploading, setIsUploading] = useState(false);
  const uploaderRef = useRef(config.uploader);
  const filesRef = useRef(files);
  const uploadOneRef = useRef<(f: TFileWithMeta) => Promise<void>>(async () => {});
  const immediateRunRef = useRef<Promise<void> | null>(null);
  const initialExtra = config.extra || {};
  filesRef.current = files;

  const emit = useCallback(
    (next: TFileWithMeta[]) => {
      setFiles(next);
    },
    [config, files],
  );

  const names = useCallback(() => files.filter((f) => f.meta.status !== 'removed').map((f) => f.meta.name), [files]);

  const attachExisting: TUploadActions['attachExisting'] = useCallback(
    (items) => {
      const next = [
        ...files,
        ...items.map((it) => ({
          file: undefined,
          meta: {
            id: it?.meta?.id || v4(),
            name: it?.meta?.name || 'server-file',
            size: it?.meta?.size,
            type: it?.meta?.type,
            uploadedAt: it?.meta?.uploadedAt,
            serverId: it?.meta?.serverId,
            status: 'external' as TUploadStatus,
            percent: 100,
            extra: it?.meta?.extra || initialExtra || { duration: 0 },
          },
        })),
      ];
      emit(next);
    },
    [files, emit],
  );

  const fileCallbackToPromise = (mediaElement: HTMLMediaElement): Promise<void> => {
    return new Promise((resolve) => {
      const settle = () => {
        mediaElement.removeEventListener('loadedmetadata', settle);
        mediaElement.removeEventListener('error', settle);
        clearTimeout(timeoutId);
        resolve();
      };

      const timeoutId = setTimeout(settle, 1000);
      mediaElement.addEventListener('loadedmetadata', settle);
      mediaElement.addEventListener('error', settle);
    });
  };

  const getAudioDuration = async (f: File) => {
    try {
      const mediaElement = f.type.startsWith('audio/') ? new Audio() : document.createElement('video');

      if (!canProbeMediaDuration(f, mediaElement.canPlayType.bind(mediaElement))) {
        return 0;
      }

      const objectUrl = URL.createObjectURL(f);
      try {
        mediaElement.src = objectUrl;
        await fileCallbackToPromise(mediaElement);
        return Number.isFinite(mediaElement.duration) ? mediaElement.duration : 0;
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch {
      console.error('[getAudioDuration]: File does not respond with duration in 1 second');
      return 0;
    }
  };

  const addFiles: TUploadActions['addFiles'] = useCallback(
    async (incoming) => {
      const nameFn = config.nameResolver || defaultNameResolver;
      const assignedNames = names();
      const preparedFiles = incoming.map((f) => {
        const reject = config.validators?.map((v) => v(f)).find(Boolean) as string | undefined;
        const name = nameFn(f, assignedNames);
        assignedNames.push(name);
        const id = v4();
        const meta: TFileWithMeta['meta'] = {
          id,
          name,
          size: f.size,
          type: f.type,
          lastModified: f && 'lastModified' in f ? f.lastModified : undefined,
          status: reject ? 'error' : 'queued',
          errorMessage: reject || undefined,
          percent: 0,
          uploadedAt: new Date().toISOString(),
        };

        return { file: f, meta };
      });

      const additions = await Promise.all(
        preparedFiles.map(async ({ file, meta }) => ({
          file,
          meta: {
            ...meta,
            extra: {
              duration: await getAudioDuration(file),
            },
          },
        })),
      );

      emit([...files, ...additions]);
    },
    [files, emit, config, names],
  );

  const patch = useCallback(
    (id: string, meta: Partial<TFileWithMeta['meta']>) => {
      setFiles((files: TFileWithMeta[]) => [
        ...files.map((prevFile) => {
          if (prevFile.meta.id === id) {
            return { ...prevFile, meta: { ...prevFile.meta, ...meta } };
          }
          return prevFile;
        }),
      ]);
    },
    [files, emit],
  );

  const removeFile: TUploadActions['removeFile'] = useCallback(
    (id, opts) => {
      const purge = opts?.purge !== false;
      if (purge) {
        emit(files.filter((x) => x.meta.id !== id));
      } else {
        patch(id, { status: 'removed' });
      }
    },
    [files, emit, patch],
  );

  const reorderFiles: TUploadActions['reorderFiles'] = useCallback((orderedIds) => {
    setFiles((prev) => {
      if (!orderedIds.length) {
        return prev;
      }
      const byId = new Map(prev.map((file) => [file.meta.id, file]));
      const ordered = orderedIds.map((id) => byId.get(id)).filter(Boolean) as TFileWithMeta[];
      const rest = prev.filter((file) => !orderedIds.includes(file.meta.id));
      return [...ordered, ...rest];
    });
  }, []);

  const mergeWithServerFiles: TUploadActions['mergeWithServerFiles'] = useCallback(
    (serverFiles) => {
      if (!config.mergeServerFiles) {
        return;
      }
      setFiles((prev) => config.mergeServerFiles!(serverFiles, prev));
    },
    [config.mergeServerFiles],
  );

  const uploadOne = useCallback(
    async (f: TFileWithMeta) => {
      if (!f.file) {
        return;
      } // skip external
      if (!uploaderRef.current) {
        throw new Error('Uploader adapter is not configured');
      }

      const controller = new AbortController();
      const onProgress = (p: number) => {
        patch(f.meta.id, { percent: Math.max(0, Math.min(99, p)) });
      };

      try {
        patch(f.meta.id, { status: 'preparing', percent: 0 });
        const params = await uploaderRef.current.requestParams({ file: f.file, meta: f.meta });
        patch(f.meta.id, { status: 'uploading', percent: 0 });
        const res = await uploaderRef.current.performUpload({
          file: f.file,
          meta: f.meta,
          params,
          onProgress,
          signal: controller.signal,
        });
        patch(f.meta.id, { status: 'done', percent: 100, serverId: res.serverId });
      } catch (e: unknown) {
        patch(f.meta.id, {
          status: 'error',
          errorMessage: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [patch],
  );
  uploadOneRef.current = uploadOne;

  const startUpload: TUploadActions['startUpload'] = useCallback(
    async (id) => {
      const f = files.find((x) => x.meta.id === id);
      if (!f) {
        return;
      }
      if (f.meta.status !== 'queued' && f.meta.status !== 'error') {
        return;
      }
      setIsUploading(true);
      await uploadOne(f);
      setIsUploading(false);
    },
    [files, uploadOne],
  );

  const startAll: TUploadActions['startAll'] = useCallback(async () => {
    setIsUploading(true);
    for (const f of files) {
      if (f.meta.status === 'queued' || f.meta.status === 'error') {
        await uploadOne(f);
      }
    }
    setIsUploading(false);
  }, [files, uploadOne]);

  useEffect(() => {
    if (mode !== 'immediate') {
      return;
    }
    if (!files.some((f) => f.meta.status === 'queued')) {
      return;
    }
    if (immediateRunRef.current) {
      return;
    }

    const runHolder: { promise: Promise<void> | null } = { promise: null };
    runHolder.promise = (async () => {
      setIsUploading(true);
      try {
        while (filesRef.current.some((f) => f.meta.status === 'queued')) {
          const queuedIds = filesRef.current.filter((f) => f.meta.status === 'queued').map((f) => f.meta.id);
          for (const id of queuedIds) {
            const current = filesRef.current.find((f) => f.meta.id === id);
            if (current?.meta.status === 'queued') {
              await uploadOneRef.current(current);
            }
          }
        }
      } finally {
        if (immediateRunRef.current === runHolder.promise) {
          immediateRunRef.current = null;
        }
        const stillBusy = filesRef.current.some(
          (f) => f.meta.status === 'queued' || f.meta.status === 'preparing' || f.meta.status === 'uploading',
        );
        if (!stillBusy) {
          setIsUploading(false);
        }
      }
    })();

    immediateRunRef.current = runHolder.promise;
  }, [mode, files]);

  const reset = useCallback(() => emit([]), [emit]);

  const setExtra: TUploadActions['setExtra'] = useCallback((id, extra) => {
    setFiles((prev) =>
      prev.map((file) => {
        if (file.meta.id !== id) {
          return file;
        }
        return {
          ...file,
          meta: {
            ...file.meta,
            extra: { ...(file.meta.extra || {}), ...extra },
          },
        };
      }),
    );
  }, []);

  const updateMeta: TUploadActions['updateMeta'] = useCallback(
    (id, metaUpdate) => {
      patch(id, metaUpdate);
    },
    [patch],
  );

  const value = useMemo(
    () => ({
      files,
      mode,
      isUploading,
      attachExisting,
      addFiles,
      removeFile,
      reorderFiles,
      mergeWithServerFiles,
      startUpload,
      startAll,
      reset,
      setExtra,
      updateMeta,
    }),
    [
      files,
      mode,
      isUploading,
      attachExisting,
      addFiles,
      removeFile,
      reorderFiles,
      mergeWithServerFiles,
      startUpload,
      startAll,
      reset,
      setExtra,
      updateMeta,
    ],
  );

  return <UploadContext.Provider value={value}> {children} </UploadContext.Provider>;
};

export const useUploadContext = () => {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    throw new Error('useUploadContext must be used inside <UploadProvider>');
  }
  return ctx;
};
